/**
 * AdsPricingPanel — embedded in the admin "Publicités" section (AdsValidation).
 * Edits ad prices + broadcast durations; instantly reflected in the PRO Ads UI.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Euro, Loader2, Image, Video, Music2, ChevronDown, ChevronUp } from 'lucide-react';
import { useAdminLang } from '../../hooks/useAdminLang';

const AdsPricingPanel = () => {
  const { at } = useAdminLang();
  const [open, setOpen] = useState(false);
  const [p, setP] = useState({
    video: { price_eur: 100, duration_days: 7 },
    audio: { price_eur: 50, duration_days: 7 },
    image: { price_eur: 50, duration_days: 7 },
    currency: 'EUR',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/ads/pricing').then(r => { setP(prev => ({ ...prev, ...r.data })); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post('/api/admin/ads/pricing', {
        video: p.video, audio: p.audio, image: p.image, currency: p.currency,
      });
      toast.success(at('adsPricingSaved') || 'Prix mis à jour — visibles immédiatement côté PRO');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const upd = (kind, field) => (e) => setP({
    ...p, [kind]: { ...p[kind], [field]: field === 'price_eur' ? parseFloat(e.target.value || 0) : parseInt(e.target.value || 0, 10) },
  });

  const Row = ({ kind, icon: Icon, label }) => (
    <div className="p-3 bg-white/5 border border-white/10 rounded-2xl space-y-2" data-testid={`ads-pricing-${kind}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-white"><Icon className="w-4 h-4 text-fuchsia-300" /> {label}</div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-[10px] text-white/50 uppercase tracking-wider mb-0.5">{at('adsPriceLabel') || 'Prix (€)'}</span>
          <input type="number" min="0" step="0.5" value={p[kind].price_eur} onChange={upd(kind, 'price_eur')}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid={`ads-pricing-${kind}-price`} />
        </label>
        <label className="block">
          <span className="block text-[10px] text-white/50 uppercase tracking-wider mb-0.5">{at('adsDurationLabel') || 'Durée (jours)'}</span>
          <input type="number" min="1" max="365" step="1" value={p[kind].duration_days} onChange={upd(kind, 'duration_days')}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid={`ads-pricing-${kind}-duration`} />
        </label>
      </div>
    </div>
  );

  return (
    <div className="mb-6 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-3xl overflow-hidden" data-testid="ads-pricing-panel">
      <button onClick={() => setOpen(v => !v)}
        className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-white/5 transition-colors"
        data-testid="ads-pricing-toggle">
        <Euro className="w-5 h-5 text-fuchsia-400" />
        <div className="flex-1">
          <p className="font-bold text-white text-sm">{at('adsPricingTitle') || 'Prix & durées des publicités'}</p>
          <p className="text-xs text-white/60 mt-0.5">{at('adsPricingSub') || 'Modifie les prix et durées. Visibles instantanément côté PRO dans Destine Ads.'}</p>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          {loading ? (
            <div className="p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" /></div>
          ) : (
            <>
              <Row kind="video" icon={Video} label={at('adsVideo') || 'Publicité vidéo'} />
              <Row kind="audio" icon={Music2} label={at('adsAudio') || 'Publicité audio (spot)'} />
              <Row kind="image" icon={Image} label={at('adsImage') || 'Publicité affiche (image)'} />
              <button onClick={save} disabled={saving}
                className="w-full py-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="ads-pricing-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {at('adsPricingSave') || 'Enregistrer'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdsPricingPanel;
